import { Random } from '../utils/Random.js';
import { playNoiseBurst } from './proceduralSynth.js';

/**
 * Digital rain bed — filtered noise hiss scaled by rain visibility & intensity.
 */
export class RainAudio {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {import('../config.js').CONFIG['audio']['rain']} config
   * @param {number} seed
   */
  constructor(ctx, bus, config, seed) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config;
    this._rng = new Random(seed);
    this._mix = 0;
    this._dripTimer = 0;
    this._started = false;

    /** @type {AudioBufferSourceNode | null} */
    this._noise = null;
    /** @type {GainNode | null} */
    this._bedGain = null;
    /** @type {BiquadFilterNode | null} */
    this._band = null;
    /** @type {BiquadFilterNode | null} */
    this._high = null;
    /** @type {OscillatorNode | null} */
    this._filterLfo = null;
  }

  start() {
    if (this._started || !this._config.enabled) return;
    this._started = true;

    const length = this._ctx.sampleRate * 3;
    const buffer = this._ctx.createBuffer(1, length, this._ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * 0.5;
    }

    this._noise = this._ctx.createBufferSource();
    this._noise.buffer = buffer;
    this._noise.loop = true;

    this._band = this._ctx.createBiquadFilter();
    this._band.type = 'bandpass';
    this._band.frequency.value = this._config.filterHz;
    this._band.Q.value = this._config.filterQ;

    this._high = this._ctx.createBiquadFilter();
    this._high.type = 'highpass';
    this._high.frequency.value = this._config.highPassHz;

    this._bedGain = this._ctx.createGain();
    this._bedGain.gain.value = 0;

    this._filterLfo = this._ctx.createOscillator();
    this._filterLfo.frequency.value = this._config.filterLfoHz ?? 0.15;
    const lfoGain = this._ctx.createGain();
    lfoGain.gain.value = this._config.filterLfoDepth ?? 420;
    this._filterLfo.connect(lfoGain);
    lfoGain.connect(this._band.frequency);
    this._filterLfo.start();

    this._noise.connect(this._high);
    this._high.connect(this._band);
    this._band.connect(this._bedGain);
    this._bedGain.connect(this._bus);
    this._noise.start();

    this._dripTimer = this._rng.range(
      this._config.dripIntervalMin,
      this._config.dripIntervalMax,
    );
  }

  /**
   * @param {number} delta
   * @param {{
   *   inSimulation?: boolean;
   *   rainActive?: boolean;
   *   rainIntensity?: number;
   * }} state
   */
  update(delta, state) {
    if (!this._started || !this._bedGain) return;

    const active =
      (state.inSimulation ?? false) && (state.rainActive ?? false);
    const intensity = Math.max(0.25, state.rainIntensity ?? 1);
    const target = active
      ? this._config.bedGain * intensity * (this._config.intensityScale ?? 1)
      : 0;

    this._mix += (target - this._mix) * Math.min(1, delta * 3.5);
    this._bedGain.gain.value = this._mix;

    if (!active || this._mix < 0.008) return;

    this._dripTimer -= delta;
    if (this._dripTimer > 0) return;
    this._dripTimer = this._rng.range(
      this._config.dripIntervalMin,
      this._config.dripIntervalMax,
    );
    if (this._rng.next() > (this._config.dripChance ?? 0.35)) return;

    playNoiseBurst(this._ctx, this._bus, {
      duration: this._rng.range(0.02, 0.045),
      gain: this._config.dripGain * this._mix * this._rng.range(0.6, 1.1),
      filterFreq: this._rng.range(1800, 5200),
      filterQ: 0.85,
    });
  }

  dispose() {
    if (this._noise) {
      try {
        this._noise.stop();
      } catch {
        /* noop */
      }
      this._noise.disconnect();
      this._noise = null;
    }
    try {
      this._filterLfo?.stop();
    } catch {
      /* noop */
    }
    this._filterLfo?.disconnect();
    this._filterLfo = null;
    this._high?.disconnect();
    this._high = null;
    this._band?.disconnect();
    this._band = null;
    this._bedGain?.disconnect();
    this._bedGain = null;
    this._started = false;
  }
}
